import { create } from "zustand";

const useCustomerManagementStore = create((set) => ({
  customerData: [],
  isLoading: false,
  error: null,

  // Helper function to get auth headers
  getAuthHeaders: () => {
    const token = localStorage.getItem("token"); // Or from your auth context
    console.log("getAuthHeaders - Token from localStorage:", token); // Debug token
    if (!token) {
      console.error("No token found in localStorage");
      return {
        headers: {
          "Content-Type": "application/json",
          Authorization: "",
        },
        params: "",
      };
    }

    const tenantId = localStorage.getItem("tenantId");
    const role = localStorage.getItem("role");
    const params = new URLSearchParams();

    if (tenantId && tenantId !== "null" && role !== "SuperAdmin") {
      params.append("tenantId", tenantId); // Only append tenantId for non-SuperAdmins
    }
    if (role === "SuperAdmin") {
      params.append("allTenants", "true"); // SuperAdmin bypasses tenancy
    }
    console.log("Constructed headers - Token:", token, "Params:", params.toString());
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    console.log("getAuthHeaders - Constructed headers:", headers, "Params:", params.toString());

    return {
      headers,
      params: params.toString(), // Convert params to query string
    };
  },

  fetchCustomers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useCustomerManagementStore.getState().getAuthHeaders();
      console.log("fetchCustomers - Received headers:", headers, "Params:", params); // Debug received values
      if (!headers || !headers.Authorization) {
        throw new Error("No valid Authorization header - Please login again");
      }

      const url = `${process.env.REACT_APP_API_URL}api/customer/getcustomers${params ? `?${params}` : ""}`;
      console.log("Fetch URL:", url, "Headers:", headers);

      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Fetch error response:", errorText);
        if (response.status === 401) {
          throw new Error("Unauthorized - Please login again");
        }
        throw new Error("Failed to fetch customers");
      }

      const data = await response.json();
      console.log("Fetch response data:", data); // Log parsed response
      console.log("Raw response (stringified):", JSON.stringify(data)); // Log for debugging

      set({ customerData: data.customers || [], isLoading: false });
    } catch (error) {
      set({ error: error.message, isLoading: false });
      // Handle unauthorized errors (e.g., redirect to login)
      if (error.message.includes("Unauthorized") || error.message.includes("No valid Authorization header")) {
        // Optionally redirect to login page here
        console.error("Unauthorized - Redirecting to login page recommended");
      } else {
        console.error("Fetch error:", error);
      }
    }
  },

  createCustomer: async (customerData) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useCustomerManagementStore.getState().getAuthHeaders();
      const url = `${process.env.REACT_APP_API_URL}api/customer/addcustomer${params ? `?${params}` : ""}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        method: "POST",
        headers: fetchHeaders,
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Create error response:", errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin role required");
        }
        throw new Error("Failed to create customer");
      }

      const data = await response.json();
      set((state) => ({
        customerData: [...state.customerData, data.customer].filter(Boolean),
        isLoading: false,
      }));
      await useCustomerManagementStore.getState().fetchCustomers(); // Refresh list
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Create error:", error);
    }
  },

  updateCustomer: async (customerId, updatedData) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useCustomerManagementStore.getState().getAuthHeaders();
      const url = `${process.env.REACT_APP_API_URL}api/customer/updatecustomer/${customerId}${params ? `?${params}` : ""}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        method: "PUT",
        headers: fetchHeaders,
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Update error response:", errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin role required");
        }
        throw new Error("Failed to update customer");
      }

      const data = await response.json();
      set((state) => ({
        customerData: state.customerData.map((customer) =>
          customer._id === customerId
            ? { ...customer, ...updatedData }
            : customer
        ),
        isLoading: false,
      }));
      await useCustomerManagementStore.getState().fetchCustomers(); // Refresh list
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Update error:", error);
    }
  },

  deleteCustomer: async (customerId) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useCustomerManagementStore.getState().getAuthHeaders();
      const url = `${process.env.REACT_APP_API_URL}api/customer/deletecustomer/${customerId}${params ? `?${params}` : ""}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);

      const response = await fetch(url, {
        method: "DELETE",
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Delete error response:", errorText);
        if (response.status === 403) {
          throw new Error("Permission denied - SuperAdmin role required");
        }
        throw new Error("Failed to delete customer");
      }

      set((state) => ({
        customerData: state.customerData.filter(
          (customer) => customer._id !== customerId
        ),
        isLoading: false,
      }));
      await useCustomerManagementStore.getState().fetchCustomers(); // Refresh list
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Delete error:", error);
    }
  },

  searchCustomer: async (searchTerm) => {
    set({ isLoading: true, error: null });
    try {
      const { headers, params } = useCustomerManagementStore.getState().getAuthHeaders();
      const query = new URLSearchParams({ ...searchTerm, ...params }).toString();
      const url = `${process.env.REACT_APP_API_URL}api/customer/searchcustomer?${query}`;
      const fetchHeaders = new Headers();
      fetchHeaders.append("Content-Type", headers["Content-Type"]);
      fetchHeaders.append("Authorization", headers.Authorization);
      
      const response = await fetch(url, {
        headers: fetchHeaders,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Search error response:", errorText);
        throw new Error("Failed to search customers");
      }

      const data = await response.json();
      set({ customerData: data.customers|| [], isLoading: false });
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.error("Search error:", error);
    }
  },
}));

export default useCustomerManagementStore;
